import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AppRestEndPoint } from 'src/app/app-restEndPoint';
import { ToastService } from 'src/app/components/toaster/toast-service';
import { fcmTids } from 'src/app/constants/testids/fcm';
import { ApiService } from 'src/app/services/api.service';
import { PusherService } from 'src/app/services/pusher.service';
import { downloadFcmPdf, FcmPdfMode } from './fcm-pdf';
import {
  FCM_SECTION_META,
  FcmQuestion,
  FcmQuestionsResponse,
  FcmRecord,
  FcmResponseAttribute,
  FcmSubmission,
  MAX_CORE_VALUES,
  SectionMeta,
  SectionMode,
  SubmissionType,
} from './models/fcm.model';

/**
 * Step IDs:
 *   intro, sN (one per content section from the payload),
 *   interstitial, waiting, result.
 * Section step IDs are `s${n}` where `n` comes from `q.section`.
 */
type Step = string;

/**
 * Friendly labels for submission types. The API gives us the values
 * (`['solo', 'couple']`) but not human-readable descriptions, so we keep
 * those here. Add an entry if the BE introduces a new type.
 */
const SUBMISSION_TYPE_LABELS: Record<string, string> = {
  solo: 'Just me — generate a statement based on my answers alone.',
  couple:
    "As a couple — your partner will also complete this, and we'll generate a combined statement.",
};

@Component({
  standalone: false,
  selector: 'app-fcm',
  templateUrl: './fcm.component.html',
  styleUrls: ['./fcm.component.scss'],
})
export class FcmComponent implements OnInit {
  readonly tids = fcmTids;
  readonly sectionMeta = FCM_SECTION_META;
  readonly maxCoreValues = MAX_CORE_VALUES;
  readonly scaleValues = [1, 2, 3, 4, 5];

  step: Step = 'intro';
  loading = false;
  submitting = false;

  submissionType: SubmissionType = 'solo';
  /** Per-section UI mode, keyed by section number from the payload. */
  sectionModes: Record<number, SectionMode> = {};
  /** True once the user has tried to advance from this section. */
  sectionTouched: Record<number, boolean> = {};

  /** Raw server response — drives all rendering. */
  questionsPayload: FcmQuestionsResponse | null = null;

  form: UntypedFormGroup;
  record: FcmRecord | null = null;

  /**
   * Full payload from `/philanthropic_profiles/show_statement`:
   *   { submission: { purpose_statement, merged_statement, ... },
   *     household, user,
   *     responses: [{ question_code, question_label, question_type,
   *                   section, text_value, answer_label, answer_value }] }
   *
   * Drives both the on-screen statement render and the advisor PDF view
   * — the advisor view uses the pre-labeled `responses[]` rows directly
   * so the FE doesn't need to translate values back to labels.
   */
  statementData: any = null;

  /**
   * The only cached derivation. Markdown parsing runs a regex over every
   * line of the statement; everything else (filtering 46 questions /
   * responses) is fast enough to recompute per change-detection cycle.
   * Rebuilt in `fetchStatement` whenever the statement text changes.
   */
  private _formattedStatementHtmlCache = '';

  /**
   * Household advisor (name + email) — shown on the thank-you screen, same
   * pattern as `SignupCompleteComponent`. Populated from BASIC_INFO.FAMILY_INFO.
   */
  advisor: { fullname?: string; email?: string } = {};

  // ── "Send this statement" card state (result page) ──
  // The BE resolves the user's own email from the access token, so we
  // only need the advisor address explicitly.
  emailSendToSelf = false;
  emailSendToAdvisor = false;
  emailAdvisorAddress = '';
  emailSending = false;
  emailStatus = '';
  emailStatusOk = false;

  // ── Async statement generation (CUX-1841) ──
  // Submit returns 202 { status: 'generating', submission_id } and the
  // result is signalled via the per-user Pusher channel
  // `{env}_philanthropic_profile_{userId}`, event
  // `philanthropic_profile_statement`. The component then GETs
  // /show_statement and branches on submission.status.
  submissionId: number | null = null;
  /** UI state for the generating/waiting/failed screens. */
  generationStatus: 'idle' | 'generating' | 'waiting_for_partner' | 'failed' = 'idle';
  private currentUser: any = null;
  private statementChannel = '';
  private statementChannelSubscribed = false;

  constructor(
    private fb: UntypedFormBuilder,
    private apiService: ApiService,
    private toastService: ToastService,
    private router: Router,
    private pusherService: PusherService,
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    try {
      this.currentUser = JSON.parse(sessionStorage.getItem('current_user') || 'null');
    } catch {
      this.currentUser = null;
    }
    this.loadQuestions();
    this.loadAdvisor();
    // Subscribe to the per-user Pusher channel on page load so the binding
    // is in place well before the user submits — eliminates any race
    // between a fast backend and a not-yet-bound listener.
    this.subscribeToStatementChannel();
  }

  /**
   * Fetches the household advisor for the thank-you screen — mirrors the
   * SignupCompleteComponent.getFamilyInfo() pattern.
   */
  private loadAdvisor(): void {
    this.apiService.getCall(AppRestEndPoint.BASIC_INFO.FAMILY_INFO, '').subscribe((data: any) => {
      const adv = data?.household_users?.household?.advisor_user_attributes;
      if (adv) {
        this.advisor = {
          fullname: adv.admin_user_fullname,
          email: adv.admin_user_email,
        };
        // Pre-fill the "send to advisor" email field with the known
        // advisor address so the user doesn't have to type it.
        if (!this.emailAdvisorAddress && this.advisor.email) {
          this.emailAdvisorAddress = this.advisor.email;
        }
      }
    });
  }

  /**
   * POSTs the "send this statement" form to the backend
   * (`/philanthropic_profiles/email_statement`). Payload includes which
   * recipients were selected and their addresses; the BE composes and
   * sends the email(s).
   */
  sendStatement(): void {
    if (this.emailSending) return;

    if (!this.emailSendToSelf && !this.emailSendToAdvisor) {
      this.emailStatus = 'Please select at least one recipient.';
      this.emailStatusOk = false;
      return;
    }
    if (this.emailSendToAdvisor && !this.isValidEmail(this.emailAdvisorAddress)) {
      this.emailStatus = "Please enter a valid advisor email address.";
      this.emailStatusOk = false;
      return;
    }

    // Wire format (matches BE):
    //   { send_to_self, send_to_advisor, advisor_email? }
    // Server resolves the user's own email from the access token.
    const payload: Record<string, unknown> = {
      send_to_self: this.emailSendToSelf,
      send_to_advisor: this.emailSendToAdvisor,
    };
    if (this.emailSendToAdvisor) {
      payload['advisor_email'] = this.emailAdvisorAddress;
    }

    this.emailSending = true;
    this.emailStatus = '';
    this.apiService.postCall(AppRestEndPoint.FCM.SEND_STATEMENT, payload).subscribe({
      next: () => {
        this.emailSending = false;
        this.emailStatus = 'Sent successfully.';
        this.emailStatusOk = true;
      },
      error: () => {
        this.emailSending = false;
        this.emailStatus = 'We could not send the email. Please try again.';
        this.emailStatusOk = false;
      },
    });
  }

  private isValidEmail(s: string): boolean {
    return typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  }

  // ───────────────────────── data load ─────────────────────────

  /** Mirrors the risk-tolerance load pattern. */
  private loadQuestions(): void {
    this.loading = true;
    let url = AppRestEndPoint.FCM.SHOW_QUESTIONS;
    this.apiService.getCall(url, undefined).subscribe({
      next: (response: FcmQuestionsResponse) => {
        this.loading = false;
        if (!response) return;
        this.questionsPayload = response;
        this.buildFormForQuestions(response.questions || []);
      },
      error: () => { this.loading = false; },
    });
  }

  /** Maps a server `FcmRecord` to the right view step. */
  private deriveStepFromRecord(record: FcmRecord): Step {
    if (record.submission_type === 'couple' && !record.purpose_statement) return 'waiting';
    return record.purpose_statement ? 'result' : 'intro';
  }

  /**
   * Builds form controls dynamically — one per `question_code` — and
   * initializes per-section UI mode flags. Defaults mirror the data type
   * the backend will accept:
   *   - checkbox → []
   *   - slider   → 3 (mid-range)
   *   - vehicle_card / radio / textarea / madlib → ''
   */
  private buildFormForQuestions(questions: FcmQuestion[]): void {
    for (const q of questions) {
      if (this.form.contains(q.question_code)) continue;
      const initial: any =
        q.question_type === 'checkbox' ? [] :
        q.question_type === 'slider' ? 3 :
        '';
      this.form.addControl(q.question_code, new UntypedFormControl(initial));
    }
    // Seed mode state for every section the payload defines.
    for (const n of this.allSectionNumbers()) {
      if (this.sectionModes[n] === undefined) this.sectionModes[n] = 'guided';
    }
  }

  // ────────────────── payload-derived getters ──────────────────

  /** All distinct section numbers in the payload, sorted ascending. */
  allSectionNumbers(): number[] {
    const all = this.questionsPayload?.questions || [];
    return [...new Set(all.map((q) => q.section))].sort((a, b) => a - b);
  }

  /**
   * Content sections — everything except the last section in the payload,
   * which is treated as the interstitial. Falls back to `[]` until the
   * payload loads.
   */
  contentSectionNumbers(): number[] {
    const all = this.allSectionNumbers();
    return all.length > 1 ? all.slice(0, -1) : all;
  }

  /** Last section in the payload = interstitial. */
  interstitialSectionNumber(): number | null {
    const all = this.allSectionNumbers();
    return all.length > 0 ? all[all.length - 1] : null;
  }

  /** Total number of content sections — used for "Step X of N" footer. */
  totalContentSections(): number {
    return this.contentSectionNumbers().length;
  }

  /** Step ID for a section number: `s1`, `s2`, etc. */
  stepIdForSection(n: number): Step {
    return `s${n}`;
  }

  /** Submission types offered by the server (`['solo', 'couple']`). */
  availableSubmissionTypes(): string[] {
    return this.questionsPayload?.submission_types || ['solo', 'couple'];
  }

  labelForSubmissionType(value: string): string {
    return SUBMISSION_TYPE_LABELS[value] || value;
  }

  // ────────────────── per-section question lookup ──────────────────

  metaForSection(sectionNumber: number): SectionMeta | undefined {
    return this.sectionMeta.find((s) => s.section === sectionNumber);
  }

  /** Questions for a section, sorted by position. Empty until payload arrives. */
  questionsForSection(sectionNumber: number): FcmQuestion[] {
    const all = this.questionsPayload?.questions || [];
    return all
      .filter((q) => q.section === sectionNumber)
      .sort((a, b) => a.position - b.position);
  }

  /** Non-madlib questions for a section (rendered in `guided` mode). */
  guidedQuestionsFor(sectionNumber: number): FcmQuestion[] {
    return this.questionsForSection(sectionNumber).filter((q) => q.question_type !== 'madlib');
  }

  /** Madlib questions for a section (rendered in `madlibs` mode). */
  madlibQuestionsFor(sectionNumber: number): FcmQuestion[] {
    return this.questionsForSection(sectionNumber).filter((q) => q.question_type === 'madlib');
  }

  /** `*ngFor` trackBy for questions — stable by id. */
  trackByQuestionId(_i: number, q: FcmQuestion): number { return q.id; }

  /** `*ngFor` trackBy for section numbers / option values — identity is fine. */
  trackByIdentity<T>(_i: number, v: T): T { return v; }

  /** Splits a madlib template like "I give because I want ___." into prefix/suffix. */
  splitMadlib(template: string | null): { before: string; after: string } {
    if (!template) return { before: '', after: '' };
    const idx = template.indexOf('___');
    if (idx < 0) return { before: template, after: '' };
    return { before: template.slice(0, idx), after: template.slice(idx + 3) };
  }

  // ────────────────── checkbox helpers ──────────────────

  /** True when `value` is currently selected on the named checkbox control. */
  isChecked(controlName: string, value: string): boolean {
    const arr = (this.form.get(controlName)?.value as string[] | null) || [];
    return arr.includes(value);
  }

  toggleCheckbox(controlName: string, value: string, max?: number): void {
    const ctrl = this.form.get(controlName);
    if (!ctrl) return;
    const current: string[] = ctrl.value || [];
    const idx = current.indexOf(value);
    let next: string[];
    if (idx >= 0) {
      next = current.filter((v) => v !== value);
    } else {
      if (max !== undefined && current.length >= max) return; // soft cap
      next = [...current, value];
    }
    ctrl.setValue(next);
  }

  selectedCountFor(controlName: string): number {
    return ((this.form.get(controlName)?.value as string[] | null) || []).length;
  }

  // ────────────────── vehicle-card helpers ──────────────────

  /**
   * Vehicle controls are single-value selections. All three options
   * (`using`, `curious`, `neither`) are real answers — picking "Neither"
   * is an explicit opt-out, not a clear. Clicking the active button
   * toggles it off.
   */
  selectVehicle(controlName: string, value: string): void {
    const ctrl = this.form.get(controlName);
    if (!ctrl) return;
    if (ctrl.value === value) {
      ctrl.setValue('');
      return;
    }
    ctrl.setValue(value);
  }

  vehicleValue(controlName: string): string {
    return this.form.get(controlName)?.value || '';
  }

  // ────────────────── validation ──────────────────

  /** True when a question has a non-empty answer. */
  isAnswered(q: FcmQuestion): boolean {
    const value = this.form.get(q.question_code)?.value;
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  }

  /** True when every question in the given mode is answered. */
  isModeComplete(sectionNumber: number, mode: SectionMode): boolean {
    const qs = mode === 'guided'
      ? this.guidedQuestionsFor(sectionNumber)
      : this.madlibQuestionsFor(sectionNumber);
    if (qs.length === 0) return false;
    return qs.every((q) => this.isAnswered(q));
  }

  /**
   * A section is complete when EITHER the Guided tab OR the Fill-in-the-blank
   * tab is fully answered. The user only needs to complete one.
   */
  isSectionComplete(sectionNumber: number): boolean {
    return this.isModeComplete(sectionNumber, 'guided')
      || this.isModeComplete(sectionNumber, 'madlibs');
  }

  /** Show inline error on a question only after the section is touched. */
  shouldShowError(q: FcmQuestion): boolean {
    if (!this.sectionTouched[q.section]) return false;
    if (this.isSectionComplete(q.section)) return false;
    // Only flag questions in the currently visible mode — the user picks
    // one mode to fill, so we don't surface errors on the other tab.
    const mode = this.modeForSection(q.section);
    const isMadlib = q.question_type === 'madlib';
    if (mode === 'guided' && isMadlib) return false;
    if (mode === 'madlibs' && !isMadlib) return false;
    return !this.isAnswered(q);
  }

  /** First content section that's incomplete; null if all are filled. */
  private firstIncompleteSection(): number | null {
    for (const n of this.contentSectionNumbers()) {
      if (!this.isSectionComplete(n)) return n;
    }
    return null;
  }

  // ────────────────── navigation ──────────────────

  setSectionMode(sectionNumber: number, mode: SectionMode): void {
    this.sectionModes[sectionNumber] = mode;
  }

  modeForSection(sectionNumber: number): SectionMode {
    return this.sectionModes[sectionNumber] || 'guided';
  }

  goTo(step: Step): void {
    this.step = step;
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  }

  /** Ordered step list for forward/back navigation: content sections + interstitial. */
  private orderedFormSteps(): Step[] {
    const out = this.contentSectionNumbers().map((n) => this.stepIdForSection(n));
    out.push('interstitial');
    return out;
  }

  next(): void {
    const current = this.currentSectionFromStep();
    if (current !== null && !this.isSectionComplete(current)) {
      this.sectionTouched[current] = true;
      this.toastService.show('Please complete either the Guided questions or the Fill-in-the-blank tab to continue.', {
        classname: 'bg-danger text-light', delay: 3500,
      });
      return;
    }
    const order = this.orderedFormSteps();
    const idx = order.indexOf(this.step);
    if (idx >= 0 && idx < order.length - 1) this.goTo(order[idx + 1]);
  }

  /** Returns the content-section number for the current step, or null. */
  private currentSectionFromStep(): number | null {
    for (const n of this.contentSectionNumbers()) {
      if (this.step === this.stepIdForSection(n)) return n;
    }
    return null;
  }

  back(): void {
    const order: Step[] = ['intro', ...this.orderedFormSteps()];
    const idx = order.indexOf(this.step);
    if (idx > 0) this.goTo(order[idx - 1]);
  }

  startQuestionnaire(): void {
    const first = this.contentSectionNumbers()[0];
    if (first !== undefined) this.goTo(this.stepIdForSection(first));
  }

  goToDashboard(): void { this.router.navigate(['/dashboard']); }

  retake(): void {
    this.record = null;
    this.startQuestionnaire();
  }

  jumpToSection(sectionNumber: number): void {
    this.goTo(this.stepIdForSection(sectionNumber));
  }

  isSectionActive(sectionNumber: number): boolean {
    return this.step === this.stepIdForSection(sectionNumber);
  }

  isSectionDone(sectionNumber: number): boolean {
    const stepOrder: Step[] = [
      'intro', ...this.orderedFormSteps(), 'result', 'waiting',
    ];
    const currentIdx = stepOrder.indexOf(this.step);
    const sectionIdx = stepOrder.indexOf(this.stepIdForSection(sectionNumber));
    return currentIdx > sectionIdx;
  }

  currentStepNumber(): number {
    const sections = this.contentSectionNumbers();
    for (let i = 0; i < sections.length; i++) {
      if (this.step === this.stepIdForSection(sections[i])) return i + 1;
    }
    return 0;
  }

  /** True when the current step is one of the content sections. */
  isInForm(): boolean {
    return this.contentSectionNumbers().some(
      (n) => this.step === this.stepIdForSection(n),
    );
  }

  /** The interstitial section's textarea (last section in the payload). */
  interstitialQuestion(): FcmQuestion | undefined {
    const n = this.interstitialSectionNumber();
    return n == null ? undefined : this.questionsForSection(n)[0];
  }

  // ────────────────── submission ──────────────────

  generate(): void {
    if (this.submitting) return;

    const incomplete = this.firstIncompleteSection();
    if (incomplete !== null) {
      for (const n of this.contentSectionNumbers()) this.sectionTouched[n] = true;
      this.toastService.show('Some sections are still incomplete. Please finish either the Guided or Fill-in-the-blank tab in each.', {
        classname: 'bg-danger text-light', delay: 4000,
      });
      this.goTo(this.stepIdForSection(incomplete));
      return;
    }

    const payload: FcmSubmission = {
      submission_type: this.submissionType,
      philanthropic_profile_responses_attributes: this.buildResponseAttributes(),
    };
    this.submitting = true;
    // Subscribe before the POST returns so we don't race the Pusher event
    // against a fast backend.
    this.subscribeToStatementChannel();

    this.apiService.postCall(AppRestEndPoint.FCM.SUBMIT, payload).subscribe({
      next: (response: any) => {
        this.submitting = false;
        // 202 Accepted: { status: 'generating', submission_id }
        // Statement is produced asynchronously; result arrives via Pusher.
        this.submissionId = response?.submission_id ?? null;
        this.generationStatus = 'generating';
        this.goTo('generating');
      },
      error: () => {
        this.submitting = false;
        this.toastService.show('We could not save your responses. Please try again.', {
          classname: 'bg-danger text-light', delay: 4000,
        });
      },
    });
  }

  // ────────────────── async statement generation (CUX-1841) ──────────────────

  /**
   * Subscribes to the per-user Pusher channel and binds the statement
   * event. Channel is `{env}_philanthropic_profile_{userId}`; event is
   * `philanthropic_profile_statement` with payload
   * `{ submission_id, status }` where status ∈
   * completed | waiting_for_partner | failed.
   *
   * Idempotent — safe to call more than once per session.
   */
  private subscribeToStatementChannel(): void {
    if (this.statementChannelSubscribed) return;
    const userId = this.currentUser?.id;
    if (!userId) {
      console.warn('[FCM] Missing current_user.id; cannot subscribe to statement channel');
      return;
    }

    this.pusherService.subscribePhilanthropicProfileStatement(userId);
    this.pusherService.bind('philanthropic_profile_statement', (data: any) => {
      this.handleStatementEvent(data);
    });
    this.statementChannelSubscribed = true;
  }

  /**
   * Handles a `philanthropic_profile_statement` Pusher payload. Channel
   * is per-user, but we still match `submission_id` defensively in case
   * the user resubmits within the same session.
   */
  private handleStatementEvent(data: any): void {
    if (
      this.submissionId !== null &&
      data?.submission_id != null &&
      Number(data.submission_id) !== Number(this.submissionId)
    ) {
      return;
    }
    const status = data?.status;
    if (status === 'completed') {
      this.fetchStatement(() => {
        this.generationStatus = 'idle';
        this.goTo('result');
      });
    } else if (status === 'waiting_for_partner') {
      this.generationStatus = 'waiting_for_partner';
      this.goTo('waiting');
    } else if (status === 'failed') {
      this.generationStatus = 'failed';
      this.goTo('failed');
    }
  }

  /** Re-run from the failed screen. */
  retrySubmit(): void {
    this.generationStatus = 'idle';
    this.goTo('interstitial');
  }

  /**
   * Builds the `philanthropic_profile_responses_attributes` array from
   * the current form state.
   *
   *   - textarea / madlib / slider → one row with `text_value`
   *   - radio / vehicle_card       → one row with `philanthropic_profile_answer_option_id`
   *   - checkbox                   → one row per selected option (same question_id)
   *
   * Unanswered questions are skipped — blank textareas, empty arrays,
   * and cleared radios/vehicles never make it into the payload.
   */
  private buildResponseAttributes(): FcmResponseAttribute[] {
    const out: FcmResponseAttribute[] = [];
    const questions = this.questionsPayload?.questions || [];

    for (const q of questions) {
      const value = this.form.get(q.question_code)?.value;
      if (value === null || value === undefined || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;

      switch (q.question_type) {
        case 'textarea':
        case 'madlib':
        case 'slider':
          out.push({
            philanthropic_profile_question_id: q.id,
            text_value: String(value),
          });
          break;

        case 'radio':
        case 'vehicle_card': {
          const opt = q.answer_options.find((o) => o.value === value);
          if (opt) {
            out.push({
              philanthropic_profile_question_id: q.id,
              philanthropic_profile_answer_option_id: opt.id,
            });
          }
          break;
        }

        case 'checkbox': {
          if (!Array.isArray(value)) break;
          for (const v of value as string[]) {
            const opt = q.answer_options.find((o) => o.value === v);
            if (opt) {
              out.push({
                philanthropic_profile_question_id: q.id,
                philanthropic_profile_answer_option_id: opt.id,
              });
            }
          }
          break;
        }
      }
    }

    return out;
  }

  // ────────────────── statement fetch + PDF ──────────────────

  /**
   * Pulls the latest statement from `/philanthropic_profiles/show_statement`
   * and patches `record.purpose_statement` so the result page (and PDF
   * print) render the Mission / Vision / Values / Focus Areas / Giving
   * Principles / Legacy Intentions / Advisor sections.
   *
   * Accepts either a flat `{ purpose_statement, ... }` envelope or a
   * nested `{ philanthropic_profile: { ... } }` shape.
   */
  private fetchStatement(onComplete?: () => void): void {
    let url = AppRestEndPoint.FCM.SHOW_STATEMENT;
    this.apiService.getCall(url, undefined).subscribe({
      next: (response: any) => {
        this.statementData = response || null;

        // Wire shape: { submission: { purpose_statement, merged_statement, ... },
        //               household, user, responses: [...] }
        // For couple submissions the BE returns merged_statement (combined
        // with the spouse) — prefer that when present.
        const submission = response?.submission;
        if (submission) {
          const statement: string | null =
            (typeof submission.merged_statement === 'string' && submission.merged_statement) ||
            (typeof submission.purpose_statement === 'string' ? submission.purpose_statement : null);

          if (this.record) {
            this.record.purpose_statement = statement;
          } else if (statement) {
            // No prior record (e.g., direct revisit) — synthesize one so
            // the template guard `*ngIf="step === 'result' && record"`
            // doesn't keep the screen blank.
            this.record = {
              id: String(submission.id ?? ''),
              submission_type: submission.submission_type,
              answers: {},
              purpose_statement: statement,
              spouse_submitted: false,
              submitted_at: submission.completed_at ?? '',
              updated_at: submission.completed_at ?? '',
            } as FcmRecord;
          }
        }
        // Only the markdown render is expensive enough to memoize — the
        // rest of the response-derived helpers iterate ~50 items, which
        // V8 handles in microseconds per change-detection cycle.
        this._formattedStatementHtmlCache = this.renderStatementMarkdown(
          this.record?.purpose_statement ?? null,
        );
        if (onComplete) onComplete();
      },
      error: (err: any) => {
        console.warn('[FCM] show_statement failed', err);
        if (onComplete) onComplete();
      },
    });
  }

  // ─────────── response-lookup helpers (result-page profile cards) ───────────

  /** Returns the display label for a single-answer question from the response. */
  labelByCode(code: string): string {
    const r = (this.statementData?.responses || []).find((x: any) => x.question_code === code);
    if (!r) return '—';
    return r.answer_label || r.text_value || '—';
  }

  /** Multi-select: returns every answer_label rendered for a question_code. */
  multiLabelsByCode(code: string): string[] {
    const rows = (this.statementData?.responses || []).filter((x: any) => x.question_code === code);
    return rows
      .map((r: any) => r.answer_label || r.text_value || '')
      .filter((v: string) => !!v);
  }

  /** Tax sensitivity slider — formatted as "N / 5". */
  taxScoreLabel(): string {
    const r = (this.statementData?.responses || []).find((x: any) => x.question_code === 'tax_efficiency');
    return r?.text_value ? `${r.text_value} / 5` : '—';
  }

  /** All vehicle_card answers as "Vehicle name (status)" strings. */
  vehicleLabels(): string[] {
    const rows = (this.statementData?.responses || []).filter(
      (x: any) => x.question_type === 'vehicle_card' && x.answer_label,
    );
    return rows.map((r: any) => `${r.question_label} (${r.answer_label})`);
  }

  /** The "anything else?" interstitial textarea, for the Notes card. */
  additionalNotesValue(): string {
    const r = (this.statementData?.responses || []).find((x: any) => x.question_code === 'additional_notes');
    return r?.text_value || '';
  }

  /**
   * Groups `statementData.responses[]` by section for the advisor PDF view,
   * merging multiple checkbox rows for the same question into a single
   * "answer1, answer2" string. Returns sections sorted ascending.
   */
  get advisorViewSections(): Array<{
    section: number;
    title: string;
    rows: Array<{ label: string; value: string }>;
  }> {
    const responses = this.statementData?.responses;
    if (!Array.isArray(responses) || responses.length === 0) return [];

    const bySection = new Map<number, Map<string, { label: string; values: string[] }>>();
    for (const r of responses) {
      const sec = Number(r.section);
      if (!bySection.has(sec)) bySection.set(sec, new Map());
      const qMap = bySection.get(sec)!;
      if (!qMap.has(r.question_code)) {
        qMap.set(r.question_code, { label: r.question_label, values: [] });
      }
      const display = (r.text_value !== null && r.text_value !== undefined && r.text_value !== '')
        ? String(r.text_value)
        : (r.answer_label || '');
      if (display) qMap.get(r.question_code)!.values.push(display);
    }

    return Array.from(bySection.keys())
      .sort((a, b) => a - b)
      .map((section) => ({
        section,
        title: this.metaForSection(section)?.eyebrow || `Section ${section}`,
        rows: Array.from(bySection.get(section)!.values()).map((q) => ({
          label: q.label,
          value: q.values.join(', ') || '—',
        })),
      }));
  }

  /**
   * Refreshes the statement from `/show_statement` and then generates a
   * PDF entirely from the response data — pdfmake creates the document
   * programmatically, so the file isn't a DOM screenshot and isn't
   * subject to print-stylesheet quirks.
   */
  printAs(mode: FcmPdfMode): void {
    this.fetchStatement(() => {
      if (!this.statementData) {
        this.toastService.show('Statement data is not available yet. Please try again in a moment.', {
          classname: 'bg-danger text-light', delay: 4000,
        });
        return;
      }
      try {
        downloadFcmPdf(mode, this.statementData);
      } catch (err) {
        console.error('[FCM] PDF generation failed', err);
        this.toastService.show('We could not generate the PDF. Please try again.', {
          classname: 'bg-danger text-light', delay: 4000,
        });
      }
    });
  }

  // ────────────────── result-screen display helpers ──────────────────

  // ────────────────── purpose-statement markdown rendering ──────────────────

  /**
   * Backend returns the statement as markdown (`## Mission`, `## Vision`, ...,
   * `- **Value name** — description`). Bind via [innerHTML] in the template.
   * Mirrors the prototype's renderMarkdown() so the rendered structure matches.
   *
   * Angular's DomSanitizer keeps the safe tags we emit (h3, ul, li, p,
   * strong, em, hr, div).
   */
  get formattedStatementHtml(): string {
    return this._formattedStatementHtmlCache;
  }

  private renderStatementMarkdown(text: string | null): string {
    if (!text) return '';
    const lines = text.split('\n');
    let html = '';
    let inUl = false;
    const closeUl = () => {
      if (inUl) { html += '</ul>'; inUl = false; }
    };

    for (const line of lines) {
      // Top-level H1 — skip; the doc title is already in `.ai-doc-header`.
      if (/^# [^#]/.test(line)) {
        closeUl();
        continue;
      }
      if (/^## (.+)/.test(line)) {
        closeUl();
        const heading = line.replace(/^## /, '');
        if (heading === 'For Your Forum Advisor') {
          html += '<hr class="ai-advisor-sep" />';
          html += '<div class="ai-advisor-eyebrow">For Your Forum Advisor</div>';
        } else {
          html += '<h3>' + this.escapeAndFormat(heading) + '</h3>';
        }
        continue;
      }
      if (/^---+$/.test(line.trim())) {
        closeUl();
        html += '<hr />';
        continue;
      }
      if (/^- (.+)/.test(line)) {
        if (!inUl) { html += '<ul>'; inUl = true; }
        html += '<li>' + this.escapeAndFormat(line.replace(/^- /, '')) + '</li>';
        continue;
      }
      if (line.trim() === '') {
        closeUl();
        continue;
      }
      closeUl();
      html += '<p>' + this.escapeAndFormat(line) + '</p>';
    }
    closeUl();
    return html;
  }

  private escapeAndFormat(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+?)\*/g, '<em>$1</em>');
  }
}
